import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { SPC_LINKS } from '../mock';
import api, { TOKEN_KEY, errMsg } from '../api';

const SpcContext = createContext(null);
export const useSpc = () => useContext(SpcContext);
const PUSH_OPT_OUT_KEY = 'spc_push_opt_out';
const BOOTSTRAP_CACHE_KEY = 'spc_bootstrap_cache_v1';
const PENDING_BOOKING_CONFIRM_KEY = 'spc_pending_booking_confirm';
const PENDING_BLOCK_CONFIRM_KEY = 'spc_pending_block_confirm';

const EMPTY = {
  auth: { mode: 'login' },
  member: {}, sessions: [], listings: [], notifications: [], conversations: [], members: [],
  services: [], discounts: [], rewards: [], pointRules: [], referrals: [],
  challenge: { progress: 0, target: 0, title: '', unit: '' }, challenges: [], pendingChallengeSubmissions: [],
  announcements: [],
  adminData: { attendance: {}, waitlists: {}, reminders: {}, completedEvents: [], challengeResults: [], pointLedger: [], redemptions: [] },
  achievementCatalogue: [], achievementProgress: {}, unlockedAchievements: [], role: 'member',
  settings: { attendancePoints: 25 },
  apexInterest: { count: 0, interested: false, members: [] },
  parkrun: { date: null, myLocation: null, locations: [], groups: [] },
  recentActivity: [],
  liveEvents: [],
};

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function SpcProvider({ children }) {
  const [state, setState] = useState(EMPTY);
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);
  const [toastMsg, setToastMsg] = useState('');
  const [modal, setModal] = useState(null);
  const [raceHub, setRaceHub] = useState({ open: false, mode: 'view' });
  const [celebration, setCelebration] = useState(null);
  const [celebrationQueue, setCelebrationQueue] = useState([]);
  const [pendingView, setPendingView] = useState(null);
  const [pendingFocus, setPendingFocus] = useState(null);
  const [busy, setBusy] = useState(0);
  const toastTimer = useRef(null);

  const toast = useCallback((msg) => {
    setToastMsg(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(''), 3200);
  }, []);

  const applyResponse = useCallback((data) => {
    if (data.state) {
      setState(prev => {
        // Merge onto prev (not replace) so slim poll responses that omit large static
        // fields (e.g. achievementCatalogue) don't wipe them from memory.
        const next = { ...prev, ...data.state, auth: prev.auth };
        // Detect achievements unlocked since last snapshot & queue a celebration.
        // Only fire if we already have a hydrated state — skip the very first load.
        try {
          const wasHydrated = !!(prev && (prev.achievementCatalogue || []).length > 0);
          if (wasHydrated) {
            const prevIds = new Set((prev.unlockedAchievements || []).map(a => a.id));
            const newly = (next.unlockedAchievements || []).filter(a => !prevIds.has(a.id));
            if (newly.length) {
              const catalogue = next.achievementCatalogue || [];
              const details = newly.map(n => catalogue.find(c => c.id === n.id)).filter(Boolean);
              if (details.length) {
                setCelebration(details[0]);
                if (details.length > 1) setCelebrationQueue(q => [...q, ...details.slice(1)]);
              }
            }
            // Surface a pop-up (not just the bell) when a "starts in 2 hours" reminder lands —
            // this doesn't rely on email, so it works even while email delivery is unreliable.
            const prevNotifIds = new Set((prev.notifications || []).map(n => n.id));
            const newReminder = (next.notifications || []).find(n => !prevNotifIds.has(n.id) && n.type === 'session_reminder_2h');
            const newCheckin = (next.notifications || []).find(n => !prevNotifIds.has(n.id) && n.type === 'attendance');
            if (newReminder) setModal({ type: 'sessionReminder', payload: { title: newReminder.title, body: newReminder.body } });
            else if (newCheckin) setModal({ type: 'checkedIn', payload: { title: newCheckin.title, body: newCheckin.body } });
            // Small confetti burst the moment one of MY challenge entries gets approved
            const prevChallenges = prev.challenges || [];
            const approvedNow = (next.challenges || []).find(nc => {
              const pc = prevChallenges.find(c => c.id === nc.id);
              return pc && (nc.myApproved || 0) > (pc.myApproved || 0);
            });
            if (approvedNow) {
              confetti({ particleCount: 90, spread: 70, startVelocity: 32, origin: { y: 0.75 }, colors: ['#e1e20a', '#ffffff', '#0a0a0a'], zIndex: 200 });
              toast(`🎉 "${approvedNow.title}" entry approved${approvedNow.pointsAward ? ` — +${approvedNow.pointsAward} pts` : ''}!`);
            }
          }
        } catch (_) {}
        return next;
      });
    }
    if (data.user) setUser(data.user);
  }, []);

  const call = useCallback(async (promise, okMsg) => {
    setBusy(b => b + 1);
    try { const { data } = await promise; applyResponse(data); if (okMsg) toast(okMsg); return data; }
    catch (e) { toast(errMsg(e)); return null; }
    finally { setBusy(b => Math.max(0, b - 1)); }
  }, [applyResponse, toast]);

  // ---- push ----
  const subscribePush = useCallback(async (requestIfNeeded, announce = !!requestIfNeeded) => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') {
      if (announce) toast('Push not supported on this browser');
      return false;
    }
    try {
      if (Notification.permission === 'denied') {
        if (announce) toast('Notifications are blocked — enable them in your phone/browser Settings → Notifications');
        return false;
      }
      if (Notification.permission !== 'granted') {
        if (!requestIfNeeded) return false;
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') { if (announce) toast('Notifications not enabled'); return false; }
      }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get('/push/public-key');
      let sub = await reg.pushManager.getSubscription();
      if (sub) {
        // A subscription that was created under a DIFFERENT VAPID public key (e.g. after a
        // key/config change) is permanently rejected by the push service (403) forever —
        // reusing it blindly means "re-enable push" never actually fixes anything. Detect a
        // key mismatch and force a clean resubscribe instead of trusting the stale one.
        try {
          const currentKey = new Uint8Array(sub.options.applicationServerKey);
          const expectedKey = urlBase64ToUint8Array(data.publicKey);
          const same = currentKey.length === expectedKey.length && currentKey.every((b, i) => b === expectedKey[i]);
          if (!same) { await sub.unsubscribe(); sub = null; }
        } catch (e) { console.error('[SPC push] key-match check failed, reusing existing subscription:', e); }
      }
      if (!sub) {
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(data.publicKey) });
      }
      await api.post('/push/subscribe', { subscription: sub.toJSON() });
      localStorage.removeItem(PUSH_OPT_OUT_KEY);
      if (announce) toast('Push notifications on 🔔');
      return true;
    } catch (e) {
      console.error('[SPC push] subscribe failed:', e);
      if (announce) toast('Could not enable push — check notification permission in your browser settings');
      return false;
    }
  }, [toast]);

  const enablePush = useCallback(() => subscribePush(true, true), [subscribePush]);

  const disablePush = useCallback(async () => {
    try {
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await api.delete('/push/subscribe', { params: { endpoint: sub.endpoint } });
          await sub.unsubscribe();
        }
      }
      localStorage.setItem(PUSH_OPT_OUT_KEY, '1');
      toast('Push notifications off');
      return true;
    } catch (e) {
      console.error('[SPC push] disable failed:', e);
      toast('Could not turn off push notifications');
      return false;
    }
  }, [toast]);

  const sendTestPush = useCallback(async () => {
    try {
      let endpoint = null;
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        endpoint = sub?.endpoint || null;
      }
      await api.post('/push/test', { endpoint });
      toast('Test push sent to this device — check your notifications 🔔');
      return true;
    } catch (e) {
      console.error('[SPC push] test push failed:', e);
      const detail = e?.response?.data?.detail || '';
      // The backend already deleted the DB record for a definitive (403/404/410) failure —
      // also drop the local browser subscription so the NEXT "enable push" creates a genuinely
      // new one instead of silently re-registering the same permanently-rejected subscription.
      if (/subscription removed/i.test(detail) && 'serviceWorker' in navigator) {
        try {
          const reg = await navigator.serviceWorker.ready;
          const sub = await reg.pushManager.getSubscription();
          if (sub) await sub.unsubscribe();
        } catch (e2) { console.error('[SPC push] local unsubscribe-after-removal failed:', e2); }
      }
      toast(detail || 'Could not send test push');
      return false;
    }
  }, [toast]);

  // ---- PWA install ----
  const installPrompt = useRef(null);
  const [canInstall, setCanInstall] = useState(false);
  useEffect(() => {
    const h = (e) => { e.preventDefault(); installPrompt.current = e; setCanInstall(true); };
    const installed = () => { installPrompt.current = null; setCanInstall(false); };
    window.addEventListener('beforeinstallprompt', h);
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', h);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);
  const installApp = useCallback(async () => {
    const dp = installPrompt.current;
    if (dp) {
      dp.prompt();
      try { await dp.userChoice; } catch (e) {}
      installPrompt.current = null;
      setCanInstall(false);
    } else if (/iphone|ipad|ipod/i.test(navigator.userAgent)) {
      openModal('iosInstall');
    } else {
      toast('Open your browser menu and choose “Add to Home Screen” to install SPC.');
    }
  }, [toast]);

  // ---- payments ----
  const startCheckout = useCallback(async () => {
    try { const { data } = await api.post('/payments/checkout', { origin_url: window.location.origin }); if (data.checkout_url) window.location.href = data.checkout_url; }
    catch (e) { toast(errMsg(e)); }
  }, [toast]);
  const startSessionCheckout = useCallback(async (sid, qty = 1, guestNames = [], dietaryNote = null) => {
    try {
      const { data } = await api.post(`/sessions/${sid}/checkout`, { origin_url: window.location.origin, qty, guestNames, dietaryNote });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) { toast(errMsg(e)); throw e; }
  }, [toast]);
  const startBlockCheckout = useCallback(async (sid) => {
    try {
      const { data } = await api.post(`/sessions/${sid}/block-checkout`, { origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) { toast(errMsg(e)); }
  }, [toast]);
  const checkPaymentStatus = (sid) => api.get(`/payments/status/${sid}`);
  const fetchSessionRoster = async (sid) => {
    try { const { data } = await api.get(`/sessions/${sid}/roster`); return data; }
    catch (e) { return null; }
  };
  const fetchListingPhotos = async (lid) => {
    try { const { data } = await api.get(`/listings/${lid}/photos`); return data; }
    catch (e) { return null; }
  };
  const fetchSubmissionPhotos = async (sid) => {
    try { const { data } = await api.get(`/admin/challenges/submissions/${sid}/photos`); return data; }
    catch (e) { return null; }
  };

  // ---- boot ----
  const [connectionError, setConnectionError] = useState(false);
  const bootCancelRef = useRef({ cancelled: false });
  const runBoot = useCallback(async (cancelToken) => {
    let graceTimer = null;
    let shownFromCache = false;
    setConnectionError(false);
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      // Prefer fresh data with no flash — but a slow/stalled connection must never leave the
      // user stuck on the boot screen indefinitely. If the live fetch hasn't resolved within a
      // short grace period AND we have a last-known snapshot, show that now (so the app is
      // usable) while the live fetch keeps running in the background and silently updates the
      // view the moment it actually resolves. On a normal connection this grace timer never
      // fires, so the common case still always shows fresh data on first render.
      try {
        const cached = localStorage.getItem(BOOTSTRAP_CACHE_KEY);
        if (cached) {
          graceTimer = setTimeout(() => {
            if (cancelToken.cancelled || shownFromCache) return;
            try { applyResponse(JSON.parse(cached)); } catch (_) {}
            shownFromCache = true;
            setBooting(false);
          }, 3500);
        }
      } catch (_) {}

      // Retry transient failures — only drop the token when the server says it's invalid (401/403).
      let loaded = false;
      let authFailed = false;
      for (let attempt = 0; attempt < 3 && !cancelToken.cancelled; attempt++) {
        try {
          const { data } = await api.get('/bootstrap');
          if (!cancelToken.cancelled) {
            applyResponse(data);
            try { localStorage.setItem(BOOTSTRAP_CACHE_KEY, JSON.stringify(data)); } catch (_) {}
            if (localStorage.getItem(PUSH_OPT_OUT_KEY) !== '1') subscribePush(false, false);
          }
          loaded = true;
          break;
        } catch (e) {
          const status = e?.response?.status;
          if (status === 401 || status === 403) {
            authFailed = true;
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(BOOTSTRAP_CACHE_KEY);
            if (!cancelToken.cancelled) { setUser(null); setState(EMPTY); }
            break;
          }
          if (attempt < 2) await new Promise(r => setTimeout(r, 600));
        }
      }
      if (graceTimer) clearTimeout(graceTimer);
      if (!loaded && !authFailed && !cancelToken.cancelled && !shownFromCache) {
        // All 3 live attempts failed for a NON-auth reason (timeout/network) and there was
        // nothing cached to fall back to. The token is still perfectly valid — this is just a
        // bad connection — so do NOT fall through to the login screen (that looks exactly like
        // being signed out, even though the session is fine). Show a dedicated retry screen instead.
        try {
          const cached = localStorage.getItem(BOOTSTRAP_CACHE_KEY);
          if (cached) applyResponse(JSON.parse(cached));
          else setConnectionError(true);
        } catch (_) {}
      }
    }
    if (!cancelToken.cancelled) setBooting(false);
  }, [applyResponse, subscribePush]);

  useEffect(() => {
    const cancelToken = { cancelled: false };
    bootCancelRef.current = cancelToken;
    runBoot(cancelToken);
    return () => { cancelToken.cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retryBoot = useCallback(() => {
    bootCancelRef.current.cancelled = true; // supersede any still-running previous attempt
    const cancelToken = { cancelled: false };
    bootCancelRef.current = cancelToken;
    setBooting(true);
    runBoot(cancelToken);
  }, [runBoot]);

  // ---- polling for cross-device updates ----
  const pollNow = useCallback(async () => {
    try { const { data } = await api.get('/bootstrap?poll=true'); applyResponse(data); } catch (e) {}
  }, [applyResponse]);

  useEffect(() => {
    if (!user) return;
    // Randomised jitter so many open devices don't all poll the server in the same instant
    const jitter = Math.floor(Math.random() * 4000);
    const id = setInterval(pollNow, 15000 + jitter);
    return () => clearInterval(id);
  }, [user, pollNow]);

  // After a paid session-booking checkout redirects back in, show the same
  // "You're booked!" pop-up free bookings get, once the session data is loaded.
  useEffect(() => {
    const pendingId = localStorage.getItem(PENDING_BOOKING_CONFIRM_KEY);
    if (!pendingId || !state.sessions?.length) return;
    const s = state.sessions.find(x => x.id === pendingId);
    if (s) {
      localStorage.removeItem(PENDING_BOOKING_CONFIRM_KEY);
      openModal('bookingConfirmed', { session: s });
    }
  }, [state.sessions]);

  // Same for a paid block-booking checkout — show every booked week once loaded.
  useEffect(() => {
    const pendingSeriesId = localStorage.getItem(PENDING_BLOCK_CONFIRM_KEY);
    if (!pendingSeriesId || !state.sessions?.length) return;
    const todayISO = new Date().toISOString().slice(0, 10);
    const weeks = state.sessions.filter(s => s.seriesId === pendingSeriesId && s.mine && s.dateISO >= todayISO).sort((a, b) => a.dateISO.localeCompare(b.dateISO));
    if (weeks.length) {
      localStorage.removeItem(PENDING_BLOCK_CONFIRM_KEY);
      openModal('bookingConfirmed', { sessions: weeks });
    }
  }, [state.sessions]);

  const role = user ? (user.role === 'Admin' ? 'admin' : 'member') : 'member';
  const getBenefits = useCallback(() => api.get('/benefits'), []);
  const getMembersDirectory = useCallback(() => api.get('/members/directory'), []);
  const startConversation = async (recipientId) => {
    const data = await call(api.post('/conversations/start', { recipientId }));
    if (data && data.conversationId) { openModal('thread', { id: data.conversationId }); }
  };

  // ---- auth ----
  const authTab = (mode) => setState(s => ({ ...s, auth: { ...s.auth, mode } }));
  const login = async (email, password, remember = true) => {
    setBusy(b => b + 1);
    try {
      const { data } = await api.post('/auth/login', { email: String(email).trim(), password, remember: !!remember });
      localStorage.setItem(TOKEN_KEY, data.token);
      applyResponse(data);
      try { localStorage.setItem(BOOTSTRAP_CACHE_KEY, JSON.stringify(data)); } catch (_) {}
      if (localStorage.getItem(PUSH_OPT_OUT_KEY) !== '1') subscribePush(false, false);
    } catch (e) { toast(errMsg(e)); }
    finally { setBusy(b => Math.max(0, b - 1)); }
  };
  const signup = async (name, email, password, referralCode, discountCode) => {
    setBusy(b => b + 1);
    try {
      const body = { name, email: String(email).trim(), password };
      if (referralCode && referralCode.trim()) body.referralCode = referralCode.trim().toUpperCase();
      if (discountCode && discountCode.trim()) body.discountCode = discountCode.trim().toUpperCase();
      const { data } = await api.post('/auth/register', body);
      localStorage.setItem(TOKEN_KEY, data.token);
      applyResponse(data);
      if (data.user?.status === 'Active') {
        toast('Discount code applied — your membership is active. Welcome to SPC!');
      } else {
        startCheckout();
      }
    } catch (e) { toast(errMsg(e)); }
    finally { setBusy(b => Math.max(0, b - 1)); }
  };
  const markWhatsappPromptSeen = () => api.post('/me/whatsapp-prompt-seen').catch(() => {});
  const logout = () => { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(BOOTSTRAP_CACHE_KEY); setUser(null); setState(EMPTY); };
  const forgotPassword = async (email) => {
    setBusy(b => b + 1);
    try {
      await api.post('/auth/forgot-password', { email: String(email).trim(), origin_url: window.location.origin });
      toast("If that email is registered, we've sent a password reset link.");
      authTab('login');
    } catch (e) { toast(errMsg(e)); }
    finally { setBusy(b => Math.max(0, b - 1)); }
  };
  const resetPassword = async (token, password) => {
    setBusy(b => b + 1);
    try {
      await api.post('/auth/reset-password', { token, password });
      toast('Password updated — you can now sign in.');
      return true;
    } catch (e) { toast(errMsg(e)); return false; }
    finally { setBusy(b => Math.max(0, b - 1)); }
  };

  const openSPC = (key) => { const url = SPC_LINKS[key]; if (url) window.open(url, '_blank', 'noopener'); };
  const copy = (text, msg) => { try { navigator.clipboard?.writeText(text); } catch (e) {} toast(msg || 'Copied'); };

  // ---- race hub / modal ----
  const openRaceHub = (mode = 'view') => setRaceHub({ open: true, mode });
  const closeRaceHub = () => setRaceHub(r => ({ ...r, open: false }));
  const setRaceHubMode = (mode) => setRaceHub(r => ({ ...r, mode }));
  const openModal = (type, payload) => setModal({ type, payload });
  const closeModal = () => setModal(null);
  const navigateTo = (view) => setPendingView(view);
  const consumePendingView = () => setPendingView(null);

  // Notification click while the app tab is already open — service worker posts the target URL.
  // It also posts 'spc-refresh' right after showing any push, so an open tab picks up the new
  // data (e.g. a check-in) within a second or two instead of waiting for the next poll tick.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMsg = (event) => {
      if (event.data?.type === 'spc-navigate' && event.data.url) {
        try {
          const nav = new URL(event.data.url).searchParams.get('nav');
          if (nav) setPendingView(nav);
        } catch (e) {}
      } else if (event.data?.type === 'spc-refresh' && user) {
        pollNow();
      }
    };
    navigator.serviceWorker.addEventListener('message', onMsg);
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, [pollNow, user]);
  const focusSession = (sid) => { setPendingFocus(sid); setPendingView('book'); };
  const consumeFocus = () => setPendingFocus(null);
  const closeCelebration = () => {
    setCelebrationQueue(q => {
      if (q.length > 0) {
        const [nextOne, ...rest] = q;
        setCelebration(nextOne);
        return rest;
      }
      setCelebration(null);
      return q;
    });
  };

  // ---- sessions ----
  const bookSession = useCallback(async (id, paceGroup, dietaryNote) => {
    setState(prev => ({
      ...prev,
      sessions: prev.sessions.map(s => s.id === id ? { ...s, mine: true, booked: Math.min(s.capacity, s.booked + 1) } : s),
    }));
    const data = await call(api.post(`/sessions/${id}/book`, { paceGroup: paceGroup || null, dietaryNote: dietaryNote || null }));
    if (!data) {
      setState(prev => ({
        ...prev,
        sessions: prev.sessions.map(s => s.id === id ? { ...s, mine: false, booked: Math.max(0, s.booked - 1) } : s),
      }));
    } else {
      const s = (data.state?.sessions || []).find(x => x.id === id);
      if (s) openModal('bookingConfirmed', { session: s });
    }
    return data;
  }, [call]);
  const updateDietaryNote = useCallback((sid, note) => call(api.post(`/sessions/${sid}/dietary-note`, { note: note || '' }), 'Dietary note saved'), [call]);
  const cancelBooking = useCallback(async (id) => {
    setState(prev => ({
      ...prev,
      sessions: prev.sessions.map(s => s.id === id ? { ...s, mine: false, booked: Math.max(0, s.booked - 1) } : s),
    }));
    const data = await call(api.post(`/sessions/${id}/cancel`));
    if (!data) {
      setState(prev => ({
        ...prev,
        sessions: prev.sessions.map(s => s.id === id ? { ...s, mine: true, booked: Math.min(s.capacity, s.booked + 1) } : s),
      }));
    }
    return data;
  }, [call]);
  const bookBlock = useCallback(async (id) => {
    const target = state.sessions.find(x => x.id === id);
    const seriesId = target?.seriesId;
    const data = await call(api.post(`/sessions/${id}/book-block`));
    if (data?.blockBookingResult) {
      const r = data.blockBookingResult;
      toast(r.skippedFull > 0 ? `Booked ${r.booked} of ${r.total} weeks — ${r.skippedFull} full` : `Booked into all ${r.booked} upcoming weeks`);
      if (seriesId) {
        const todayISO = new Date().toISOString().slice(0, 10);
        const weeks = (data.state?.sessions || []).filter(s => s.seriesId === seriesId && s.mine && s.dateISO >= todayISO).sort((a, b) => a.dateISO.localeCompare(b.dateISO));
        if (weeks.length) openModal('bookingConfirmed', { sessions: weeks });
      }
    }
    return data;
  }, [call, toast, state.sessions, openModal]);
  const rejoinBlockWeek = (id) => call(api.post(`/sessions/${id}/rejoin-week`), "You're back in for this week!");
  const addSession = (data) => call(api.post('/sessions', data), 'Session created');
  const deleteSession = (id, force = false) => call(api.delete(`/sessions/${id}${force ? '?force=true' : ''}`), force ? 'Session deleted' : 'Session cancelled');
  const editSession = (id, data) => call(api.patch(`/sessions/${id}`, data), 'Session updated');
  const cancelSeries = (id) => call(api.delete(`/series/${id}`), 'Weekly series ended');

  // ---- listings ----
  const addListing = (data) => call(api.post('/listings', { title: data.title, condition: data.condition, price: data.price, category: data.category, photos: data.photos || [], description: data.description || '' }), 'Listing posted — members notified');
  const editListing = (id, data) => call(api.patch(`/listings/${id}`, { title: data.title, condition: data.condition, category: data.category, photos: data.photos, description: data.description }), 'Listing updated');
  const removeListing = (id) => call(api.delete(`/listings/${id}`), 'Listing removed');
  const setListingStatus = (id, status) => call(api.patch(`/listings/${id}/status`, { status }), status === 'available' ? 'Marked available' : status === 'reserved' ? 'Marked reserved' : 'Marked sold');
  const reduceListingPrice = (id, price) => call(api.patch(`/listings/${id}/price`, { price }), 'Price reduced — nice one!');

  // ---- messaging ----
  const notifyInterest = async (listing, offerAmount) => {
    const data = await call(api.post(`/listings/${listing.id}/interest`, offerAmount ? { offerAmount } : {}));
    if (data && data.conversationId) { toast(offerAmount ? `Offer sent to ${listing.seller}` : `Ping sent to ${listing.seller}`); openModal('thread', { id: data.conversationId }); }
  };
  const sendMessage = useCallback(async (cid, text, image) => {
    const optimisticMsg = { from: 'me', text: text || '', image: image || null, time: 'Just now' };
    setState(prev => ({
      ...prev,
      conversations: prev.conversations.map(c => c.id === cid ? { ...c, messages: [...c.messages, optimisticMsg] } : c),
    }));
    const data = await call(api.post(`/conversations/${cid}/messages`, { text: text || '', image: image || null }));
    if (!data) {
      setState(prev => ({
        ...prev,
        conversations: prev.conversations.map(c => c.id === cid ? { ...c, messages: c.messages.filter(m => m !== optimisticMsg) } : c),
      }));
    }
    return data;
  }, [call]);
  const markConversationRead = (cid) => call(api.post(`/conversations/${cid}/read`));
  const reactToMessage = useCallback(async (cid, mid, emoji) => {
    if (!mid) return; // still-sending optimistic message, no id yet — ignore taps until it lands
    const toggle = (m) => m.id !== mid ? m : { ...m, myReaction: m.myReaction === emoji ? null : emoji };
    setState(prev => ({
      ...prev,
      conversations: prev.conversations.map(c => c.id !== cid ? c : { ...c, messages: c.messages.map(toggle) }),
    }));
    const data = await call(api.post(`/conversations/${cid}/messages/${mid}/react`, { emoji }));
    if (!data) {
      // roll back the optimistic toggle on failure
      setState(prev => ({
        ...prev,
        conversations: prev.conversations.map(c => c.id !== cid ? c : { ...c, messages: c.messages.map(toggle) }),
      }));
    }
  }, [call]);

  // ---- notifications ----
  const markNotificationsRead = () => call(api.post('/notifications/read'));
  // Optimistic: clear the list instantly on tap instead of waiting on the round-trip (which can
  // lag on a slow/flaky connection and make the button look like it "did nothing"); roll back
  // to the previous list only if the request actually fails.
  const clearNotifications = useCallback(async () => {
    let prevNotifications;
    setState(prev => { prevNotifications = prev.notifications; return { ...prev, notifications: [] }; });
    try {
      const { data } = await api.delete('/notifications');
      applyResponse(data);
    } catch (e) {
      setState(prev => ({ ...prev, notifications: prevNotifications }));
      toast(errMsg(e));
    }
  }, [applyResponse, toast]);
  const updateAvatar = (image) => call(api.patch('/me/avatar', { image: image || null }), image ? 'Profile photo updated' : 'Profile photo removed');
  const updateNotificationPrefs = (prefs) => call(api.patch('/me/notification-prefs', { prefs }), 'Notification settings saved');
  const updateLeaderboard = (settings) => call(api.patch('/me/leaderboard', settings), 'Leaderboard settings saved');
  const changeEmail = (newEmail, currentPassword) => call(api.patch('/auth/change-email', { newEmail, currentPassword }), 'Email address updated');
  const changePassword = (currentPassword, newPassword) => call(api.patch('/auth/change-password', { currentPassword, newPassword }), 'Password updated');
  const getLeaderboard = () => api.get('/leaderboard');
  const getHallOfFame = () => api.get('/hall-of-fame');
  const crownChampion = (label) => call(api.post('/admin/season/crown-champion', { label: label || '' }), 'Champion crowned — SPC Points reset for the new season 🏆');
  const getGames = (poll) => api.get(`/games${poll ? '?poll=true' : ''}`);
  const getGamesPresence = () => api.get('/games/presence');
  const raceDayComplete = async (payload) => { setBusy(b => b + 1); try { const { data } = await api.post('/games/raceday/complete', payload); return data; } catch (e) { toast(errMsg(e)); return null; } finally { setBusy(b => Math.max(0, b - 1)); } };
  const getRaceDayLeaderboard = async () => { try { const { data } = await api.get('/games/raceday/leaderboard'); return data; } catch { return null; } };
  const createGame = (opponentId) => { setBusy(b => b + 1); return api.post('/games', { opponentId: opponentId || null }).finally(() => setBusy(b => Math.max(0, b - 1))); };
  const joinGame = (id) => { setBusy(b => b + 1); return api.post(`/games/${id}/join`).finally(() => setBusy(b => Math.max(0, b - 1))); };
  const makeMove = (id, cell) => { setBusy(b => b + 1); return api.post(`/games/${id}/move`, { cell }).finally(() => setBusy(b => Math.max(0, b - 1))); };
  const leaveGame = (id) => api.delete(`/games/${id}`);

  // ---- email templates (admin) ----
  const getEmailTemplates = () => api.get('/admin/email-templates');
  const updateEmailTemplate = async (key, payload) => {
    setBusy(b => b + 1);
    try { await api.patch(`/admin/email-templates/${key}`, payload); toast('Template saved'); return true; }
    catch (e) { toast(errMsg(e)); return false; }
    finally { setBusy(b => Math.max(0, b - 1)); }
  };
  const resetEmailTemplate = async (key) => {
    setBusy(b => b + 1);
    try { await api.post(`/admin/email-templates/${key}/reset`); toast('Reset to default'); return true; }
    catch (e) { toast(errMsg(e)); return false; }
    finally { setBusy(b => Math.max(0, b - 1)); }
  };
  const sendTestEmailTemplate = async (key) => {
    setBusy(b => b + 1);
    try { const { data } = await api.post(`/admin/email-templates/${key}/test`); toast(data.message); return true; }
    catch (e) { toast(errMsg(e)); return false; }
    finally { setBusy(b => Math.max(0, b - 1)); }
  };
  const toggleEmailTemplate = async (key, enabled) => {
    setBusy(b => b + 1);
    try { await api.patch(`/admin/email-templates/${key}/toggle`, { enabled }); toast(enabled ? 'Email turned on' : 'Email turned off'); return true; }
    catch (e) { toast(errMsg(e)); return false; }
    finally { setBusy(b => Math.max(0, b - 1)); }
  };

  // ---- broadcasts (admin) ----
  const getBroadcasts = () => api.get('/admin/broadcasts');
  const deleteBroadcast = async (id) => {
    setBusy(b => b + 1);
    try { await api.delete(`/admin/broadcasts/${id}`); toast('Broadcast removed from every member\'s bell'); return true; }
    catch (e) { toast(errMsg(e)); return false; }
    finally { setBusy(b => Math.max(0, b - 1)); }
  };

  // ---- recent admin actions / undo ----
  const getAuditLog = () => api.get('/admin/audit-log');
  const undoAuditAction = async (id) => {
    setBusy(b => b + 1);
    try {
      const { data } = await api.post(`/admin/audit-log/${id}/undo`);
      applyResponse(data);
      if (data.undoWarnings?.length) toast(`Partly undone — ${data.undoWarnings[0]}`);
      else toast('Action undone');
      return data;
    } catch (e) { toast(errMsg(e)); return null; }
    finally { setBusy(b => Math.max(0, b - 1)); }
  };

  // ---- rewards / achievements ----
  const redeemReward = (id) => { const r = state.rewards.find(x => x.id === id); call(api.post(`/rewards/${id}/redeem`), r ? r.title + ' redeemed' : undefined); };
  const unlockAchievement = async (id) => {
    const a = state.achievementCatalogue.find(x => x.id === id);
    if (!a || isAchievementUnlocked(id)) return;
    const data = await call(api.post(`/achievements/${id}/unlock`));
    if (data) setCelebration(a);
  };

  // ---- admin ----
  const approveMember = (id) => call(api.patch(`/admin/members/${id}/approve`), 'Member approved');
  const createMember = (data) => call(api.post('/admin/members', data), 'Member created');
  const updateMember = (id, data) => call(api.patch(`/admin/members/${id}`, data), 'Member updated');
  const bulkImportMembers = (rows) => call(api.post('/admin/members/bulk-import', { rows }));
  const bulkImportMembersFile = (formData) => call(api.post('/admin/members/bulk-import-file', formData));
  const deleteMember = (id) => call(api.delete(`/admin/members/${id}`), 'Member deleted');
  const promoteToAdmin = (id) => call(api.post(`/admin/members/${id}/promote-admin`), 'Promoted to full admin');
  const resetClubData = () => call(api.post('/admin/reset'), 'Clean sweep complete — all club data wiped');
  const launchReset = () => call(api.post('/admin/launch-reset'), 'Reset complete — bookings kept, everything else fresh');
  const toggleMemberStatus = (id) => call(api.patch(`/admin/members/${id}/toggle-status`), 'Member status updated');
  const sendRenewalReminder = (id) => call(api.post(`/admin/members/${id}/remind-renewal`), 'Renewal reminder sent');
  const toggleCoachPermission = (id) => call(api.patch(`/admin/members/${id}/toggle-coach`));
  const toggleScanPermission = (id) => call(api.patch(`/admin/members/${id}/toggle-scan`));
  const createRole = (payload) => call(api.post('/admin/roles', payload), 'Role created');
  const updateRole = (id, payload) => call(api.patch(`/admin/roles/${id}`, payload), 'Role updated');
  const deleteRole = (id) => call(api.delete(`/admin/roles/${id}`), 'Role deleted');
  const markAttendance = (id) => call(api.post(`/admin/sessions/${id}/attendance`));
  const scanAttendance = async (sid, code) => {
    setBusy(b => b + 1);
    try {
      const { data } = await api.post(`/admin/sessions/${sid}/scan`, { code });
      if (data.ok && !data.already) {
        setState(prev => ({
          ...prev,
          sessions: prev.sessions.map(s => s.id === sid ? {
            ...s,
            attended: data.attended,
            attendedMembers: [...(s.attendedMembers || []), { id: data.memberId, name: data.memberName }],
          } : s),
        }));
      }
      return data;
    } catch (e) { toast(errMsg(e)); return null; }
    finally { setBusy(b => Math.max(0, b - 1)); }
  };
  const updateSettings = (data) => call(api.patch('/admin/settings', data), 'Settings saved');
  const markWelcomeSeen = () => call(api.post('/me/welcome-seen'));
  const replayWelcome = () => call(api.post('/me/replay-welcome'), 'Welcome tour will play on your next login');
  const awardAchievement = (mid, aid) => call(api.post(`/admin/members/${mid}/award/${aid}`), 'Achievement awarded');
  const revokeAchievement = (mid, aid) => call(api.delete(`/admin/members/${mid}/award/${aid}`), 'Achievement removed');
  const getCurrentPoll = () => api.get('/polls/current');
  const votePoll = async (pid, optionIds) => {
    setBusy(b => b + 1);
    try { const { data } = await api.post(`/polls/${pid}/vote`, { optionIds }); toast('Vote saved'); return data; }
    catch (e) { toast(errMsg(e)); return null; }
    finally { setBusy(b => Math.max(0, b - 1)); }
  };
  const createManualPoll = () => call(api.post('/admin/polls/manual'), 'Weekly poll created');
  const updateChallenge = (data) => call(api.patch('/admin/challenge', data), 'Challenge saved');
  const editChallenge = (cid, data) => call(api.patch(`/admin/challenge/${cid}`, data), 'Challenge updated');
  const endChallenge = (cid, payload) => call(api.delete(`/admin/challenge/${cid}`, { data: payload || {} }), 'Challenge closed');
  const purgeChallenge = (cid) => call(api.delete(`/admin/challenges/${cid}/purge`), 'Challenge removed');
  const submitChallenge = (cid, data) => call(api.post(`/challenges/${cid}/submit`, data), 'Entry submitted — awaiting admin approval');
  const reviewSubmission = (sid, status, comment) => call(api.patch(`/admin/challenges/submissions/${sid}`, { status, comment }), status === 'approved' ? 'Entry approved' : status === 'pending' ? 'Sent back for another look' : status === 'needs_changes' ? 'Sent back to member for changes' : 'Entry rejected');
  const resubmitChallenge = (sid, data) => call(api.patch(`/challenges/submissions/${sid}/resubmit`, data), 'Updated — sent back for review');
  const promoteWaitlist = (id) => call(api.post(`/admin/sessions/${id}/promote`), 'Member promoted');
  const toggleReminder = (id) => call(api.post(`/admin/sessions/${id}/toggle-reminder`));
  const toggleApexInterest = () => call(api.post('/apex/interest'));
  const openApexWindow = async (payload) => {
    const data = await call(api.post('/admin/apex/open-window', payload || {}));
    if (data && typeof data.notified === 'number') toast(`Apex window opened — ${data.notified} member${data.notified === 1 ? '' : 's'} notified`);
    return data;
  };
  const attendParkrun = (location) => call(api.post('/parkrun/attend', { location }), "You're in!");
  const cancelParkrun = () => call(api.post('/parkrun/cancel'));
  const deleteParkrunLocation = (id) => call(api.delete(`/admin/parkrun/locations/${id}`), 'Location removed');
  const postAnnouncement = (text, link, externalUrl) => call(api.post('/admin/announcements', { text, link: link || null, externalUrl: externalUrl || null }), 'Posted to club activity');
  const removeAnnouncement = (id) => call(api.delete(`/admin/announcements/${id}`), 'Post removed');
  const removeActivity = (id) => call(api.delete(`/admin/activity/${encodeURIComponent(id)}`), 'Removed from club activity');
  const editActivity = (id, text) => call(api.patch(`/admin/activity/${encodeURIComponent(id)}`, { text }), 'Club activity updated');
  const addPointRule = (payload) => call(api.post('/admin/point-rules', payload), 'Point rule added');
  const getChallengeLibrary = async () => { try { const r = await api.get('/admin/challenge-library'); return r.data.library; } catch { return []; } };
  const toggleLibraryItem = async (key) => { try { await api.post(`/admin/challenge-library/toggle/${encodeURIComponent(key)}`); return true; } catch { return false; } };
  const addService = (payload) => call(api.post('/admin/services', payload), 'Service added');
  const updateService = (id, payload) => call(api.patch(`/admin/services/${id}`, payload), 'Service updated');
  const removeService = (id) => call(api.delete(`/admin/services/${id}`), 'Service removed');
  const generateListingCaption = async (photo, title, category) => {
    try {
      const { data } = await api.post('/listings/generate-caption', { photo, title, category });
      return data.caption;
    } catch (e) { toast(errMsg(e)); return null; }
  };
  const generateAnnouncementDraft = async (prompt) => {
    try {
      const { data } = await api.post('/admin/announcements/generate-draft', { prompt });
      return data.draft;
    } catch (e) { toast(errMsg(e)); return null; }
  };
  const fetchCoachChatHistory = async () => {
    try { const { data } = await api.get('/coach-chat/history'); return (data.messages || []).map(m => ({ role: m.role, text: m.text })); }
    catch { return []; }
  };
  const clearCoachChat = async () => { try { await api.delete('/coach-chat/history'); } catch (e) { toast(errMsg(e)); } };
  const streamCoachChat = async (text, onDelta) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coach-chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text }),
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => null);
        toast(err?.detail || 'Smartie is unavailable right now');
        return false;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let hadError = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.delta) onDelta(payload.delta);
          if (payload.error) hadError = true;
        }
      }
      if (hadError) toast('Smartie hit a snag — please try again');
      return !hadError;
    } catch (e) {
      toast('Could not reach Smartie — check your connection');
      return false;
    }
  };
  const updatePointRule = (id, payload) => call(api.patch(`/admin/point-rules/${id}`, payload), 'Point rule updated');
  const removePointRule = (id) => call(api.delete(`/admin/point-rules/${id}`), 'Point rule removed');
  const manualPoints = (member, points, reason) => call(api.post('/admin/points', { member, points: Number(points), reason }), 'Points adjustment recorded');
  const updatePointEntry = (id, delta, reason) => call(api.patch(`/admin/points/${id}`, { delta: Number(delta), reason }), 'Ledger entry updated');
  const deletePointEntry = (id) => call(api.delete(`/admin/points/${id}`), 'Ledger entry deleted');
  const uncheckIn = async (sid, uid) => {
    setBusy(b => b + 1);
    try {
      const { data } = await api.delete(`/admin/sessions/${sid}/attendance/${uid}`);
      applyResponse(data);
      toast(`${data.memberName || 'Member'} unchecked`);
      return data;
    } catch (e) { toast(errMsg(e)); return null; }
    finally { setBusy(b => Math.max(0, b - 1)); }
  };
  const fulfilRedemption = (id) => call(api.post(`/admin/redemptions/${id}/fulfil`), 'Reward fulfilled');
  const addDiscount = (data) => call(api.post('/admin/discounts', data), 'Offer added');
  const updateDiscount = (id, data) => call(api.patch(`/admin/discounts/${id}`, data), 'Offer updated');
  const removeDiscount = (id) => call(api.delete(`/admin/discounts/${id}`), 'Offer removed');
  const addReward = (data) => call(api.post('/admin/rewards', { title: data.title, cost: Number(data.cost), description: data.description, photos: data.photos || [] }), 'Reward added');
  const updateReward = (id, data) => call(api.patch(`/admin/rewards/${id}`, { title: data.title, cost: Number(data.cost), description: data.description, photos: data.photos || [] }), 'Reward updated');
  const removeReward = (id) => call(api.delete(`/admin/rewards/${id}`), 'Reward removed');
  const createDiscountCode = (code, percentOff) => call(api.post('/admin/discount-codes', { code, percentOff: Number(percentOff) }), 'Discount code created');
  const toggleDiscountCode = (code) => call(api.patch(`/admin/discount-codes/${encodeURIComponent(code)}/toggle`), 'Discount code updated');
  const deleteDiscountCode = (code) => call(api.delete(`/admin/discount-codes/${encodeURIComponent(code)}`), 'Discount code deleted');

  // ---- resource library ----
  const getResources = async () => { try { const r = await api.get('/resources'); return r.data.resources; } catch { return []; } };
  const uploadResource = (formData) => call(api.post('/admin/resources', formData), 'Resource uploaded');
  const updateResource = (id, data) => call(api.patch(`/admin/resources/${id}`, data), 'Resource updated');
  const removeResource = (id) => call(api.delete(`/admin/resources/${id}`), 'Resource removed');
  const resourceFileUrl = (id) => `${api.defaults.baseURL}/resources/${id}/file?auth=${localStorage.getItem(TOKEN_KEY) || ''}`;
  const sessionPhotoUrl = (id, v) => `${api.defaults.baseURL}/sessions/${id}/photo?auth=${localStorage.getItem(TOKEN_KEY) || ''}&v=${v || 0}`;
  const challengePhotoUrl = (id, v) => `${api.defaults.baseURL}/challenges/${id}/photo?auth=${localStorage.getItem(TOKEN_KEY) || ''}&v=${v || 0}`;
  const getResourceTopics = async () => { try { const r = await api.get('/resource-topics'); return r.data.topics; } catch { return []; } };
  const addResourceTopic = (name) => call(api.post('/admin/resource-topics', { name }), 'Topic added');
  const removeResourceTopic = (name) => call(api.delete(`/admin/resource-topics/${encodeURIComponent(name)}`), 'Topic removed');

  // ---- Coaches / Leaders hub ----
  const getCoachHubResources = async () => { try { const r = await api.get('/coach-hub/resources'); return r.data.resources; } catch { return []; } };
  const getCoachHubRoutes = async () => { try { const r = await api.get('/coach-hub/routes'); return r.data.routes; } catch { return []; } };
  const createRoute = (formData) => call(api.post('/admin/routes', formData), 'Route added');
  const updateRoute = (id, data) => call(api.patch(`/admin/routes/${id}`, data), 'Route updated');
  const removeRoute = (id) => call(api.delete(`/admin/routes/${id}`), 'Route removed');
  const routePhotoUrl = (id, v) => `${api.defaults.baseURL}/routes/${id}/photo?auth=${localStorage.getItem(TOKEN_KEY) || ''}&v=${v || 0}`;
  const routeGpxUrl = (id) => `${api.defaults.baseURL}/routes/${id}/gpx?auth=${localStorage.getItem(TOKEN_KEY) || ''}`;
  const getOnlineCount = async () => { try { const r = await api.get('/members/online-count'); return r.data.count; } catch { return 0; } };
  const setBreakingNews = (formData) => call(api.post('/admin/breaking-news', formData), 'Breaking news posted');
  const clearBreakingNews = () => call(api.delete('/admin/breaking-news'), 'Breaking news cleared');
  const markBreakingNewsSeen = (id) => api.post('/breaking-news/seen', { id }).catch(() => {});
  const breakingNewsMediaUrl = () => `${api.defaults.baseURL}/breaking-news/media?auth=${localStorage.getItem(TOKEN_KEY) || ''}`;
  const adminAddMemberToSession = (sid, userId) => call(api.post(`/admin/sessions/${sid}/add-member`, { userId }), 'Member added — no payment taken');
  const adminAddGuestToSession = (sid, name) => call(api.post(`/admin/sessions/${sid}/add-guest`, { name }), 'Guest added');
  const adminRemoveGuestFromSession = (sid, gid) => call(api.delete(`/admin/sessions/${sid}/remove-guest/${gid}`), 'Guest removed');
  const adminTransferMember = (sid, fromUserId, toUserId, mode) => call(api.post(`/admin/sessions/${sid}/transfer-member`, { fromUserId, toUserId, mode }), 'Booking transferred');
  const adminRemoveMemberFromSession = (sid, uid) => call(api.delete(`/admin/sessions/${sid}/remove-member/${uid}`), 'Member removed from session');

  // ---- achievement helpers ----
  const isAchievementUnlocked = (id) => (state.unlockedAchievements || []).some(x => x.id === id);
  const achievementEarnedDate = (id) => ((state.unlockedAchievements || []).find(x => x.id === id) || {}).earnedAt || 'Unlocked';
  const achievementMetricValue = (a) => Number((state.achievementProgress || {})[a.metric] || 0);

  // ---- safety card & SPC Live ----
  const saveSafetyCard = (payload) => call(api.patch('/me/safety-card', payload), 'Safety card saved');
  const setMemberPin = (pin) => call(api.patch('/me/pin', { pin }), 'Your PIN is set — this is now also your referral code!');
  const getSessionSafetyCards = (sid) => api.get(`/sessions/${sid}/safety-cards`);
  const postLiveUpdate = (key, text) => call(api.post('/race-hub/live-updates', { key, text }));

  // ---- club feed ----
  const getFeed = async () => { try { const r = await api.get('/feed'); return r.data.posts; } catch { return []; } };
  const createFeedPost = (formData) => call(api.post('/feed', formData), 'Posted to the Club Feed');
  const deleteFeedPost = (id) => call(api.delete(`/feed/${id}`), 'Post removed');
  const toggleFeedPin = (id) => api.patch(`/feed/${id}/pin`).then(r => r.data.post).catch(() => null);
  const reactFeedPost = (id, type) => api.post(`/feed/${id}/react`, { type }).then(r => r.data.post).catch(() => null);
  const addFeedComment = (id, text, gif) => api.post(`/feed/${id}/comments`, { text, gif: gif || null }).then(r => r.data.post).catch(() => null);
  const deleteFeedComment = (pid, cid) => api.delete(`/feed/${pid}/comments/${cid}`).then(r => r.data.post).catch(() => null);
  const feedMediaUrl = (pid, idx) => `${api.defaults.baseURL}/feed/${pid}/media/${idx}?auth=${localStorage.getItem(TOKEN_KEY) || ''}`;
  const markFeedSeen = () => call(api.patch('/me/feed-seen'));

  const value = {
    state, user, role, booting, connectionError, retryBoot, toastMsg, modal, raceHub, celebration, pendingView, pendingFocus, busy,
    toast, authTab, login, signup, markWhatsappPromptSeen, logout, forgotPassword, resetPassword, openSPC, copy,
    openRaceHub, closeRaceHub, setRaceHubMode, openModal, closeModal, closeCelebration, unlockAchievement,
    navigateTo, consumePendingView, focusSession, consumeFocus,
    bookSession, cancelBooking, bookBlock, rejoinBlockWeek, updateDietaryNote,
    approveMember, createMember, updateMember, bulkImportMembers, bulkImportMembersFile, deleteMember, promoteToAdmin, resetClubData, launchReset, toggleMemberStatus, sendRenewalReminder, markAttendance, scanAttendance, updateChallenge, editChallenge, endChallenge, purgeChallenge, submitChallenge, reviewSubmission, resubmitChallenge, updateSettings, markWelcomeSeen, replayWelcome, awardAchievement, revokeAchievement, getCurrentPoll, votePoll, createManualPoll, promoteWaitlist, toggleReminder, toggleApexInterest, openApexWindow, attendParkrun, cancelParkrun, deleteParkrunLocation, postAnnouncement, removeAnnouncement, removeActivity, editActivity, addPointRule, updatePointRule, removePointRule, getChallengeLibrary, toggleLibraryItem, addService, updateService, removeService, generateListingCaption, generateAnnouncementDraft, fetchCoachChatHistory, clearCoachChat, streamCoachChat,
    toggleCoachPermission, toggleScanPermission, createRole, updateRole, deleteRole, manualPoints, updatePointEntry, deletePointEntry, uncheckIn, fulfilRedemption,
    addSession, editSession, deleteSession, cancelSeries, addListing, editListing, removeListing, setListingStatus, reduceListingPrice, addDiscount, updateDiscount, removeDiscount,
    addReward, updateReward, removeReward, redeemReward, createDiscountCode, toggleDiscountCode, deleteDiscountCode,
    getResources, uploadResource, updateResource, removeResource, resourceFileUrl, sessionPhotoUrl, challengePhotoUrl, getOnlineCount,
    getResourceTopics, addResourceTopic, removeResourceTopic, setBreakingNews, clearBreakingNews, markBreakingNewsSeen, breakingNewsMediaUrl,
    getCoachHubResources,
    getCoachHubRoutes, createRoute, updateRoute, removeRoute, routePhotoUrl, routeGpxUrl,
    adminAddMemberToSession, adminRemoveMemberFromSession, adminTransferMember, adminAddGuestToSession, adminRemoveGuestFromSession,
    notifyInterest, markNotificationsRead, clearNotifications, sendMessage, markConversationRead, reactToMessage, updateAvatar, updateNotificationPrefs, updateLeaderboard, changeEmail, changePassword, getLeaderboard, getHallOfFame, crownChampion, getGames, getGamesPresence, raceDayComplete, getRaceDayLeaderboard, createGame, joinGame, makeMove, leaveGame, getBenefits, getMembersDirectory, startConversation, getEmailTemplates, updateEmailTemplate, resetEmailTemplate, sendTestEmailTemplate, toggleEmailTemplate, getBroadcasts, deleteBroadcast, getAuditLog, undoAuditAction,
    isAchievementUnlocked, achievementEarnedDate, achievementMetricValue,
    enablePush, disablePush, sendTestPush, installApp, canInstall, startCheckout, startSessionCheckout, startBlockCheckout, checkPaymentStatus, fetchSessionRoster, fetchListingPhotos, fetchSubmissionPhotos, SPC_LINKS,
    saveSafetyCard, getSessionSafetyCards, postLiveUpdate, setMemberPin,
    getFeed, createFeedPost, deleteFeedPost, toggleFeedPin, reactFeedPost, addFeedComment, deleteFeedComment, feedMediaUrl, markFeedSeen,
  };
  return <SpcContext.Provider value={value}>{children}</SpcContext.Provider>;
}
